<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import AppButton from '@/components/atoms/AppButton'

/**
 * AppMenuPanel — outer shell for complex dropdown panels.
 *
 * Two modes:
 *
 *   1. Plain panel (default) — visual shell only.
 *      Slots: trigger ({ open }), default, footer ({ close }).
 *      Existing consumers (export menu, publish menu, etc.) sit here.
 *
 *   2. Applyable filter panel (opt-in via `applyable` prop) — adds a
 *      built-in staged-commit value model so each filter dropdown stops
 *      reimplementing the pending → apply → close dance.
 *
 *      Extra props: `value` (v-model:value, scalar or array), `multi`,
 *      `applyLabel`, `cancelLabel`.
 *
 *      Default slot receives `{ pending, toggle, isSelected }` so each
 *      option row wires itself with one click handler:
 *
 *        <AppMenuPanel v-model:open="open" v-model:value="sort" applyable>
 *          <template #trigger="{ open }">…</template>
 *          <template #default="{ toggle, isSelected }">
 *            <button v-for="o in options" @click="toggle(o.value)"
 *                    :class="{ 'bg-blue-50': isSelected(o.value) }">
 *              {{ o.label }}
 *            </button>
 *          </template>
 *        </AppMenuPanel>
 *
 *      Apply emits update:value with the staged value and closes.
 *      Cancel (and click-outside in applyable mode) discards pending and closes.
 *      Opening the panel resets pending from the current committed value.
 *
 *      Override the default footer by providing the `#footer` slot — you get
 *      `{ close, apply, cancel, pending }` to wire custom action rows.
 */
const props = defineProps({
  // ── Shell (existing) ─────────────────────────────────────────────────────
  open:       { type: Boolean, default: false },
  placement:  { type: String,  default: 'bottom-end' },
  width:      { type: String,  default: 'w-72' },
  extraClass: { type: String,  default: '' },
  panelStyle: { type: Object,  default: null },
  /**
   * Teleport the panel to <body> and position it `fixed` from the trigger's
   * rect. Use when the trigger lives inside a stacking context (a `sticky`/
   * `z-*` header, a `transform`ed ancestor) that would otherwise trap the
   * panel beneath fixed page chrome — the bottom pagination bar (`z-100`) or
   * the CDP support widget (`z-[9999]`). zIndex 10000 clears both. Mirrors
   * BaseListbox's `usePortal`.
   */
  portal:     { type: Boolean, default: false },

  // ── Opt-in staged-commit mode ────────────────────────────────────────────
  applyable:   { type: Boolean, default: false },
  value:       { type: [String, Number, Array, Boolean, null], default: null },
  multi:       { type: Boolean, default: false },
  applyLabel:  { type: String,  default: 'Apply' },
  cancelLabel: { type: String,  default: 'Cancel' },
})

const emit = defineEmits(['update:open', 'update:value'])

// ── Staging state ───────────────────────────────────────────────────────────
const seedPending = () => props.multi
  ? (Array.isArray(props.value) ? [...props.value] : [])
  : props.value

const pending = ref(seedPending())

// ── Portal positioning ───────────────────────────────────────────────────────
const rootRef     = ref(null)
const panelRef    = ref(null)
const portalStyle = ref({})

const computePortalPosition = () => {
  const el = rootRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const gap = 4
  const MARGIN = 8
  const vw = window.innerWidth
  // zIndex 10000 sits above the pagination bar (z-100) and support widget (9999).
  const style = { position: 'fixed', zIndex: 10000 }
  if (props.placement.startsWith('top')) style.bottom = `${window.innerHeight - r.top + gap}px`
  else                                    style.top    = `${r.bottom + gap}px`

  // Anchor to the trigger, then CLAMP so the panel never spills past the
  // viewport — an off-screen fixed panel adds horizontal scroll and visibly
  // shifts the page when it opens (esp. wide mobile panels near a left/mid trigger).
  const panelWidth = panelRef.value?.offsetWidth || 0
  if (props.placement.endsWith('start')) {
    let left = r.left
    if (panelWidth) left = Math.min(left, vw - panelWidth - MARGIN)
    style.left = `${Math.max(left, MARGIN)}px`
  } else {
    let right = vw - r.right
    if (panelWidth) right = Math.min(right, vw - panelWidth - MARGIN)
    style.right = `${Math.max(right, MARGIN)}px`
  }
  portalStyle.value = style
}

const onViewportChange = () => { if (props.open && props.portal) computePortalPosition() }

const teardownViewportListeners = () => {
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
}

// Reset pending whenever the panel opens — pending represents "what the user
// is staging this session", and we always start it from the current value.
// In portal mode also (re)compute the fixed position + track viewport changes.
watch(() => props.open, async (isOpen) => {
  if (isOpen) {
    pending.value = seedPending()
    if (props.portal) {
      await nextTick()
      computePortalPosition()
      window.addEventListener('resize', onViewportChange)
      window.addEventListener('scroll', onViewportChange, true)
    }
  } else {
    teardownViewportListeners()
  }
})

onBeforeUnmount(teardownViewportListeners)

const isSelected = (v) => props.multi
  ? Array.isArray(pending.value) && pending.value.includes(v)
  : pending.value === v

const toggle = (v) => {
  if (props.multi) {
    if (!Array.isArray(pending.value)) pending.value = []
    const i = pending.value.indexOf(v)
    if (i >= 0) pending.value.splice(i, 1)
    else pending.value.push(v)
  } else {
    pending.value = v
  }
}

const close = () => emit('update:open', false)

const apply = () => {
  emit('update:value', props.multi ? [...pending.value] : pending.value)
  close()
}

const cancel = () => {
  // For non-applyable consumers this just closes — seedPending() returns the
  // (unused) initial value and no `update:value` is emitted.
  pending.value = seedPending()
  close()
}

const onOutsideClick = (event) => {
  if (rootRef.value?.contains(event.target)) return

  /**
   * A panel that isn't actually on screen doesn't get to close itself.
   *
   * Some consumers mount the SAME panel twice against one shared open-state —
   * e.g. `ImportCards` renders once in the desktop toolbar and again, with
   * `hideTrigger`, inside the mobile "···" menu. Only one of the two is
   * visible (the other sits under a `hidden`/`xl:hidden` wrapper), but both
   * were listening: opening from the visible trigger immediately tripped the
   * hidden twin's outside-click — the click is outside ITS root — and the menu
   * shut in the same tick.
   *
   * `display: none` collapses the box to 0×0, which `position: fixed`
   * (portal mode) doesn't affect — unlike `offsetParent`, which is null for
   * fixed elements and would misread a perfectly visible portal panel.
   */
  const rect = panelRef.value?.getBoundingClientRect()
  if (!rect?.width && !rect?.height) return

  cancel()
}

// ── Placement ───────────────────────────────────────────────────────────────
const placementClass = computed(() => {
  const placements = {
    'bottom-end':   'top-full right-0 mt-0.5',
    'bottom-start': 'top-full left-0 mt-0.5',
    'top-end':      'bottom-full right-0 mb-0.5',
    'top-start':    'bottom-full left-0 mb-0.5',
  }
  return placements[props.placement] ?? placements['bottom-end']
})
</script>

<template>
  <div class="relative" ref="rootRef">
    <!-- Trigger -->
    <slot name="trigger" :open="open" />

    <!-- Panel — teleported to body in portal mode so a stacking-context-bound
         trigger (sticky header, etc.) can't trap it under fixed page chrome. -->
    <Teleport to="body" :disabled="!portal">
      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="open"
          ref="panelRef"
          v-click-outside="{ handler: onOutsideClick, events: ['click', 'touchstart'] }"
          :class="[
            (portal || panelStyle) ? 'fixed' : 'absolute',
            'z-[9999] bg-white border border-gray-200 rounded-lg overflow-hidden shadow-[0_2px_10px_rgba(9,38,67,0.12)]',
            (portal || panelStyle) ? '' : placementClass,
            width, extraClass
          ]"
          :style="portal ? portalStyle : (panelStyle || {})"
        >
        <!-- Body — exposes staging helpers when applyable -->
        <div class="flex flex-col">
          <slot :pending="pending" :toggle="toggle" :is-selected="isSelected" />
        </div>

        <!-- Footer: explicit slot wins; else auto-render when applyable -->
        <div
          v-if="$slots.footer"
          class="flex items-center justify-end gap-2 px-4 pb-2 pt-[10px] border-t border-gray-200"
        >
          <slot name="footer" :close="close" :apply="apply" :cancel="cancel" :pending="pending" />
        </div>
        <div
          v-else-if="applyable"
          class="flex items-center justify-end gap-2 px-4 pb-2 pt-[10px] border-t border-gray-200"
        >
          <AppButton variant="link" size="sm" @click="cancel">{{ cancelLabel }}</AppButton>
          <AppButton variant="default" size="sm" @click="apply">{{ applyLabel }}</AppButton>
        </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
